import { useCallback, useEffect, useState } from 'react';
import { useLoadingProgress } from './useLoadingProgress';
import LoadingQueue from '@/_models/virtualStaging/LoadingQueue';

const getRandomIncrement = (progress: number) => {
  const queueLength = LoadingQueue.getInstance().getLength();

  if (progress > 30 && queueLength > 0) {
    return Math.floor((100 - progress) / queueLength);
  }
  return Math.random() < 0.7 ? 0 : 1;
};

const INTERVAL = 50;

export const useSmoothLoading = (started: boolean) => {
  const { isTourLoading, loadingProgress } = useLoadingProgress();

  const [progress, setProgress] = useState(0);
  const [isLoading, setLoading] = useState(isTourLoading);

  const updateValue = useCallback(() => {
    if (!loadingProgress || progress === 100) return;

    const almostThere = loadingProgress >= 90;

    setProgress((oldValue) => {
      const newValue = oldValue + getRandomIncrement(oldValue);

      // if the new value is greater than the actual value, freeze the loader until we get a new value
      // but if we are almost there, we can keep the loader running untill 100%
      if (newValue >= loadingProgress && !almostThere) {
        return oldValue;
      }

      return newValue > 100 ? 100 : newValue;
    });
  }, [progress, loadingProgress]);

  // The tour my load in background before the user clicks on Start
  // In this case, we shouldn't show the loader at all
  useEffect(() => {
    if (!started) return;

    if (!isTourLoading) {
      setLoading(false);
      setProgress(100);
    }
  }, [started, isTourLoading]);

  useEffect(() => {
    if (progress === 100 && loadingProgress === 100) {
      setLoading(false);
    }
  }, [progress, loadingProgress]);

  useEffect(() => {
    if (!started) return;

    const intervalId = setInterval(updateValue, INTERVAL);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(intervalId);
  }, [progress, updateValue, loadingProgress, started]);

  return { isLoading, progress };
};
